/**
 * The API client.
 *
 * This replaces the prototype's four window.claude.use(...) capabilities:
 * `db` became these endpoints, `user` became /api/auth/me, and `assets` and
 * `downloads` became the receipt and export routes.
 *
 * Nothing here does arithmetic. Every figure arrives already computed by the
 * server as a decimal string, which is what makes a phone, a laptop and the
 * accountant's PDF agree.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly problems?: string[],
  ) {
    super(message)
  }
}

/** The CSRF token, readable because the server writes it to a non-HttpOnly cookie. */
function csrfToken(): string {
  const match = /(?:^|;\s*)ni_money_csrf=([^;]+)/.exec(document.cookie)
  return match ? decodeURIComponent(match[1]!) : ''
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  // Built conditionally rather than with `body: undefined`, which
  // exactOptionalPropertyTypes rightly rejects: an absent body and a body of
  // `undefined` are different things to fetch().
  const init: RequestInit = {
    method,
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(method === 'GET' ? {} : { 'x-csrf-token': csrfToken() }),
    },
  }
  if (body !== undefined) init.body = JSON.stringify(body)

  const response = await fetch(url, init)

  if (response.status === 204) return undefined as T

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>

  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload['error'] === 'string' ? payload['error'] : 'Something went wrong',
      Array.isArray(payload['problems']) ? (payload['problems'] as string[]) : undefined,
    )
  }

  return payload as T
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  del: <T>(url: string) => request<T>('DELETE', url),

  me: () => request<CurrentUser>('GET', '/api/auth/me'),
  month: (month: string) => request<MonthView>('GET', `/api/months/${month}`),
  report: (month: string) => request<ReportView>('GET', `/api/months/${month}/report`),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout', {}),
}

export interface CurrentUser {
  id: string
  email: string
  name: string
  roles: string[]
  permissions: string[]
}

export interface MonthView {
  month: string
  today: string
  closed: boolean
  closedAt: string | null
  containsSampleData: boolean
  rmr: { amount: string; fromMonth: string; inherited: boolean } | null
  alert: BillAlertView
  summary: {
    revenue: {
      recurringMonthly: string
      recurringReceived: string
      recurringOutstanding: string
      additionalReceived: string
      totalReceived: string
      transfers: string
      rmrSet: boolean
    }
    spent: string
    budgeted: string
    budgetRemaining: string
    payrollScheduled: string
    leftAfterPlan: string
    investigate: { total: string; count: number }
    uncategorised: string
    categories: Array<{
      categoryId: string
      name: string
      budget: string
      spent: string
      remaining: string
      state: 'ok' | 'warn' | 'over'
      isProcessingFees: boolean
    }>
  }
  plan: {
    basis: 'recurring' | 'budget_payroll' | 'custom'
    monthlyAmount: string
    payPeriods: number
    payPeriodsSet: boolean
    wednesdayCount: number
    cardFeePct: string
    weeklyCharge: string
    weeklyNet: string
    everydayPerPeriod: string
    cushionStart: string
    cardReceived: string
    rmrSet: boolean
    rows: PlanRowView[]
  }
  bills: BillView[]
  people: PersonView[]
  incomeSources: Array<{
    id: string
    name: string
    revenueType: 'Recurring' | 'Additional'
    defaultPaymentMethod: 'Card' | 'ACH' | 'Check' | 'Other'
    expectedMonthly: string
  }>
  /** What the Recurring sources add up to each month. */
  expectedRecurringTotal: string
  categories: Array<{
    id: string
    name: string
    monthlyBudget: string
    isProcessingFees: boolean
  }>
  transactions: TransactionView[]
}

export interface AlertBillView {
  billId: string
  name: string
  amount: string
  dueDate: string
  month: string
  autopay: boolean
  daysAway: number
}

export interface BillAlertView {
  enabled: boolean
  isAlertDay: boolean
  weekday: number
  leadDays: number
  upcoming: AlertBillView[]
  upcomingTotal: string
  overdue: AlertBillView[]
  overdueTotal: string
}

export interface PlanRowView {
  wednesday: string
  thursday: string
  friday: string
  received: string
  otherRecurring: string
  additional: string
  payroll: string
  paydays: Array<{ name: string; amount: string; date: string }>
  bills: string
  billsDue: Array<{ name: string; amount: string; date: string; autopay: boolean }>
  everyday: string
  cushionAfter: string
  projected: boolean
  overdue: boolean
  short: boolean
}

export interface BillView {
  id: string
  name: string
  amount: string
  dueDay: number
  categoryId: string | null
  autopay: boolean
  dueDate: string
  paid: boolean
  status: { kind: 'paid' | 'late' | 'soon' | 'due'; label: string }
}

export interface PersonView {
  id: string
  name: string
  kind: 'Employee' | 'Owner pay' | 'Contractor'
  amount: string
  frequency: 'Weekly' | 'Biweekly' | 'Semi-monthly' | 'Monthly'
  anchorDate: string | null
  paydays: string[]
  monthTotal: string
}

export interface TransactionView {
  id: string
  occurredOn: string
  kind: 'expense' | 'income' | 'transfer'
  amount: string
  description: string
  categoryId: string | null
  bucket: 'Bills' | 'RS' | 'Investigate'
  bucketLocked: boolean
  billId: string | null
  revenueType: 'Recurring' | 'Additional' | null
  paymentMethod: 'Card' | 'ACH' | 'Check' | 'Other' | null
  incomeSourceId: string | null
  isSample: boolean
  amazon: { needsDetails: true; missing: string[] } | null
  missingReceipt: boolean
  report: {
    id: string
    buyer: string | null
    item: string | null
    purpose: string | null
    trip: string | null
    paidWith: string | null
    status: 'Needs receipt' | 'Receipt attached' | 'Categorized' | 'Reconciled'
    hasReceipt: boolean
  } | null
}

export interface ReportItem {
  id: string
  date: string
  description: string
  amount: string
  detail: string | null
}

export interface ReportView {
  month: string
  monthLabel: string
  disclaimer: string
  containsSampleData: boolean
  summary: {
    recurringMonthlyRevenue: string
    rmrSet: boolean
    recurringReceived: string
    recurringOutstanding: string
    additionalReceived: string
    totalRevenueReceived: string
    billsPaid: string
    regularSpending: string
    payrollScheduled: string
    processingFees: string
    investigate: string
    totalOutflows: string
    cushionStart: string
    remainingCash: string
    transfers: string
  }
  openItems: {
    unpaidBills: ReportItem[]
    unpaidBillsTotal: string
    investigate: ReportItem[]
    investigateTotal: string
    missingReceipts: ReportItem[]
    missingReceiptsTotal: string
    unresolvedAmazon: ReportItem[]
    unresolvedAmazonTotal: string
  }
  detail: Array<{
    date: string
    description: string
    type: string
    classification: string
    categoryOrSource: string
    method: string
    amount: string
    receiptOrStatus: string
  }>
}
